import moment from 'moment-timezone';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PreCheckResult } from 'src/components/design-system/Button';
import useAsync from 'src/hooks/useAsync';
import axiosInstance from 'src/utils/axios';

export const MessageTrigger = [
  'New',
  'Viewed',
  'Delete',
  'Undelete',
  'Read',
  'Update',
] as const;

export interface MessageAttachment {
  messageId: number;
  attachId: number;
  companyId: string | null;
  companyName: string | null;
  createdBy: string;
  creationDate: string;
  // Optional filename properties that might exist in the API response
  fileName?: string;
  name?: string;
  originalName?: string;
  originalFile?: string;
  attachmentName?: string;
  filename?: string;
  description?: string;
}

export interface MessageDetailsGenericResponse {
  messageNumber: string;
  messageId: number;
  triggerPreCheck: Trigger[];
  companyId: number;
  clientId: string;
  messageDate: Date | null;
  _messageDate: string;
  status: string;
  attachments: MessageAttachment[];
  numOfAttachments: number;
  canEditAttachments: boolean;
  // Sender information (for inbox)
  fullName?: string;
  telecomNumber1?: string;
  // Recipient information (for outbox)
  recipientFullName?: string;
  recipientPhone?: string;
  recipientTelecom?: string;
  toFullName?: string;
  toPhone?: string;
  toUserName?: string;
  toName?: string;
  isDeleted?: boolean | string;
}

export interface Trigger {
  messages: string[];
  preCheckResult: keyof typeof PreCheckResult;
  trigger: number;
}

export type EndpointType = 'inbox' | 'outbox' | 'general';

interface UseMessageDetailsGenericOptions {
  messageId: string;
  endpointType?: EndpointType;
}

const useMessageDetailsGeneric = (options: UseMessageDetailsGenericOptions) => {
  const { messageId, endpointType = 'general' } = options;
  
  const [initialMessage, setInitialMessage] = useState({} as MessageDetailsGenericResponse);
  const prevMessageIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Only create the async call if we have a truly valid message ID
  const isValidId = !!(
    messageId &&
    messageId !== '' &&
    messageId !== 'undefined' &&
    messageId !== 'null' &&
    messageId !== 'SKIP' &&
    messageId !== 'NO_MESSAGE'
  );

  // Abort any pending requests when ID becomes invalid
  useEffect(() => {
    if (!isValidId && abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, [isValidId, messageId]);

  // Create a stable function that doesn't change when messageId becomes invalid
  const stableCall = useCallback(async () => {
    // Guard: don't execute if ID is invalid
    if (!isValidId) {
      return null;
    }

    const currentId = messageId;

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      if (
        !currentId ||
        currentId === '' ||
        currentId === 'undefined' ||
        currentId === 'null' ||
        currentId === 'SKIP' ||
        currentId === 'NO_MESSAGE'
      ) {
        throw new Error(`Invalid message ID provided: "${currentId}"`);
      }

      // Determine primary and fallback endpoints based on type
      const endpoints =
        endpointType === 'inbox'
          ? [`Message/inbox/${currentId}`, `Message/${currentId}`]
          : endpointType === 'outbox'
          ? [`Message/outbox/${currentId}`, `Message/${currentId}`]
          : [`Message/${currentId}`]; // general type only uses standard endpoint

      let res;
      let lastError;

      // Try each endpoint in order
      for (let i = 0; i < endpoints.length; i++) {
        try {
          res = await axiosInstance.get<MessageDetailsGenericResponse>(endpoints[i], {
            signal,
          });
          break; // Success, exit loop
        } catch (err: any) {
          lastError = err;
          if (i === 0 && endpoints.length > 1) {
            console.warn(`First attempt failed with ${endpoints[i]} endpoint:`, {
              messageId: currentId,
              status: err?.response?.status,
              statusText: err?.response?.statusText,
              data: err?.response?.data,
              message: err?.message,
            });
          } else if (i === endpoints.length - 1) {
            // Last attempt failed
            console.error(`All endpoint attempts failed for ${endpointType}:`, {
              messageId: currentId,
              endpoints,
              lastError: {
                status: err?.response?.status,
                statusText: err?.response?.statusText,
                data: err?.response?.data,
                message: err?.message,
              },
            });
            throw err;
          }
        }
      }

      // Check if response data valid
      if (!res || !res.data) {
        console.warn('No data received in response for messageId:', currentId);
        throw new Error('No data received from API');
      }

      const response: MessageDetailsGenericResponse = {
        ...res.data,
        messageDate: res.data.messageDate ? new Date(res.data.messageDate) : res.data.messageDate,
        _messageDate: res.data.messageDate
          ? moment(res.data.messageDate).format('MM/DD/YYYY')
          : '',
      };

      setInitialMessage(response);

      return response as MessageDetailsGenericResponse;
    } catch (error: any) {
      // Don't log errors if request aborted (expected behavior)
      if (error.name === 'AbortError' || signal.aborted) {
        throw new Error('Request cancelled');
      }

      // Enhanced error logging
      console.error(`${endpointType} message details API error:`, {
        messageId: currentId,
        endpointType,
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
        response: error.response
          ? {
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data,
            }
          : 'No response object',
      });

      // Create detailed error message
      let errorDetails = 'Unknown error';
      if (error.response) {
        errorDetails = `HTTP ${error.response.status}: ${error.response.statusText}`;
        if (error.response.data?.message) {
          errorDetails += ` - ${error.response.data.message}`;
        } else if (error.response.data?.error) {
          errorDetails += ` - ${error.response.data.error}`;
        }
      } else if (error.request) {
        errorDetails = 'Network error - no response received';
      } else {
        errorDetails = error.message || 'Request setup error';
      }

      // Re-throw with more context
      const enhancedError = new Error(
        `Failed to fetch ${endpointType} message details for ID ${currentId}: ${errorDetails}`
      );
      throw enhancedError;
    } finally {
      // Clear the abort controller reference
      abortControllerRef.current = null;
    }
  }, [messageId, isValidId, endpointType]);

  // Check if this is a transition from valid to invalid ID (modal closing)
  const wasValid =
    prevMessageIdRef.current &&
    prevMessageIdRef.current !== 'NO_MESSAGE' &&
    prevMessageIdRef.current !== '' &&
    prevMessageIdRef.current !== 'undefined' &&
    prevMessageIdRef.current !== 'null';

  const isTransitionToInvalid = wasValid && !isValidId;

  // Update the ref for next comparison
  prevMessageIdRef.current = messageId;

  // Disable auto-execute to prevent duplicate API calls
  // The consuming component (modal) will manually call execute when needed
  const shouldAutoExecute = false;

  const { value, setValue, execute, isLoading } = useAsync(
    stableCall,
    shouldAutoExecute,
    undefined,
    {} as MessageDetailsGenericResponse
  );

  return {
    message: value,
    setMessage: setValue,
    getMessage: execute,
    isMessageLoading: isLoading,
    initialMessage,
  };
};

export default useMessageDetailsGeneric;
